/**
 * The hand-off client — FR-E15, T-6.10. **[gated: spec §9-7]**
 *
 * A client per module, not a growing `lib/api.ts` (waves.md §1.1).
 *
 * **There is no action here, and that is the feature.** `core-app-integration`'s
 * hand-off broker is P1 and unbuilt, so this app has nowhere to hand a case to.
 * A `handoff.start()` that posted somewhere would be the stub FR-E15 refuses by
 * name, and it would be believed — it would look exactly like a working feature
 * for as long as nobody pressed it. What exists is one read, and a button drawn
 * from it.
 *
 * **No rule is re-derived on this side.** `available` is the server's answer to
 * *"should this button exist"*, folding the live integration state and whether
 * the platform published anywhere to go; `blocked` is the one reason the firm
 * itself can clear. The screen renders the answer rather than recomputing it —
 * the shape `lib/api/cases.ts` set for `can_move_here`.
 */

import { api } from '../api'

// -- shapes ------------------------------------------------------------------

/** Why a target is not offered. `not_defined` and `not_configured` come from
 *  `app/platform/gateway.py`; the other two are this app's own. */
export type HandoffReason =
  | 'not_defined'
  | 'not_configured'
  | 'not_enabled'
  | 'authority_unreachable'
  | 'no_launch_target'

export interface HandoffTarget {
  /** The sibling app's platform slug — `itax`, `gst`, `tds`. */
  app: string
  /** The integration definition's code, `ca-erp-<app>`. */
  code: string
  label: string
  description: string
  /** core's own word: has this firm enabled the integration. */
  active: boolean
  /** **Whether to render the button at all** — `active` *and* somewhere to go. */
  available: boolean
  reason: HandoffReason | string | null
  launch_url: string | null
  /** Rendered but not pressable. Today only `no_pan` (ADR-0016 §5). */
  blocked: 'no_pan' | null
  message: string | null
}

export interface CaseHandoff {
  case_id: string
  case_number: string
  client_id: string
  client_has_pan: boolean
  targets: HandoffTarget[]
  any_available: boolean
}

export interface FirmHandoff {
  targets: HandoffTarget[]
  any_available: boolean
  registration_ask: unknown[]
  blocked_by: string
}

// -- the client --------------------------------------------------------------

export const handoff = {
  /** What this firm could hand a case to. No client, so no PAN check. */
  firm: () => api<FirmHandoff>('/api/v1/integration/handoff'),

  /** The same three, for one case. Live and uncached on both sides: a firm that
   *  has just enabled iTax reloads the case expecting to see the button, and a
   *  firm whose entitlement lapsed an hour ago must not still have one. */
  forCase: (caseId: string) =>
    api<CaseHandoff>(`/api/v1/integration/handoff/cases/${caseId}`),
}

// -- pure rules, beside the client so they can be tested without rendering ----

/**
 * The targets a case screen should actually draw.
 *
 * One function rather than a filter at the call site, because *"which of these
 * is a button"* is the whole of FR-E15's *"no stub"* and it must have exactly
 * one answer. Today it returns `[]` in every environment.
 */
export function buttons(targets: HandoffTarget[]): HandoffTarget[] {
  return targets.filter((target) => target.available)
}

/** What to say beside a button that is rendered and cannot be pressed. */
export function blockedMessage(target: HandoffTarget): string | null {
  return target.blocked ? (target.message ?? 'This client cannot be handed over yet.') : null
}
